// src/slices/trackingSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { getAxiosConfig } from '../utils/http';

// SEND TRACKING NUMBERS
export const sendTrackingNumbers = createAsyncThunk(
  'tracking/sendTrackingNumbers',
  async ({ orderId, bigNumber, email }, { getState, rejectWithValue }) => {
    try {
  const state = getState();
  const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
  const response = await axios.post('/api/orders/email/send', { orderId, bigNumber, email }, config);
      return response.data;
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Formats a v4 TrackingEvent into a single display line. `services` is the
// shipment's carrier list (Shipment.services); ev.carrier is an index into
// it. A shipment often changes hands between carriers in transit (e.g. an
// origin carrier like Cainiao handing off to a last-mile carrier), which
// produces near-duplicate-looking milestones — labeling each line with its
// carrier is what makes those distinguishable instead of confusing.
const formatTrackingEvent = (ev, services) => {
  const parts = [];
  if (ev.date) parts.push(ev.date);
  // The live API returns the per-event description under `status`; the
  // OpenAPI spec's own example documents it as `state`. Accept either so a
  // future correction on their end doesn't silently blank this out again.
  const description = ev.status || ev.state;
  if (description) parts.push(description);
  let line = parts.join(' — ');
  if (ev.location) line += ` (${ev.location})`;
  const carrierName = Array.isArray(services) && typeof ev.carrier === 'number'
    ? services[ev.carrier]?.name
    : null;
  if (carrierName) line += ` [${carrierName}]`;
  return line;
};

const POLL_INTERVAL_MS = 3000;
const MAX_POLLS = 10;

// SEND SHIPMENT TRACKING
export const sendShipmentTracking = createAsyncThunk(
  'tracking/sendShipmentTracking',
  async ({ trackingId, destinationCountry, language, zipcode }, { getState, dispatch, rejectWithValue }) => {
    if (!trackingId) {
      return rejectWithValue('Invalid shipment data');
    }

    try {
      const state = getState();
      const config = getAxiosConfig(state, { withCredentials: true, forbidAuthForGuest: true });
      let { data } = await axios.post('/api/orders/shipments/tracking', { trackingId, destinationCountry, language, zipcode }, config);

      let attempt = 0;
      while (!data.done && attempt < MAX_POLLS) {
        dispatch(trackingProgress());
        await sleep(POLL_INTERVAL_MS);
        try {
          const polled = await axios.get(`/api/orders/shipments/tracking/${data.request_id}`, config);
          data = polled.data;
        } catch (pollError) {
          // Keep whatever we already have rather than failing the whole lookup.
          break;
        }
        attempt += 1;
      }

      return { ...data, timedOut: !data.done };
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || error.message
      );
    }
  }
);

// Slice
const trackingSlice = createSlice({
  name: 'tracking',
  initialState: {
    loading: false,
    error: null,
    success: false,
    trackingHistory: [],
    trackingStatus: 'idle', // 'idle' | 'processing' | 'completed' | 'timed_out'
    trackingNumbersResult: null,
  },
  reducers: {
    resetTrackingState: (state) => {
      state.loading = false;
      state.error = null;
      state.success = false;
      state.trackingHistory = [];
      state.trackingStatus = 'idle';
      state.trackingNumbersResult = null;
    },
    trackingProgress: (state) => {
      state.trackingStatus = 'processing';
    },
  },
  extraReducers: (builder) => {
    builder
      // SEND TRACKING NUMBERS
      .addCase(sendTrackingNumbers.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
      })
      .addCase(sendTrackingNumbers.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        state.trackingNumbersResult = action.payload;
      })
      .addCase(sendTrackingNumbers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
      })

      // SEND SHIPMENT TRACKING
      .addCase(sendShipmentTracking.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.success = false;
        state.trackingStatus = 'processing';
      })
      .addCase(sendShipmentTracking.fulfilled, (state, action) => {
        state.loading = false;
        state.success = true;
        const data = action.payload || {};
        const result = Array.isArray(data.results) ? data.results[0] : null;
        const events = result?.shipment?.states;
        const services = result?.shipment?.services;
        state.trackingHistory = Array.isArray(events)
          ? events.map((ev) => formatTrackingEvent(ev, services))
          : [];
        state.trackingStatus = data.timedOut ? 'timed_out' : (data.done ? 'completed' : 'processing');
      })
      .addCase(sendShipmentTracking.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
        state.success = false;
        state.trackingStatus = 'idle';
      });
  },
});

export const { resetTrackingState, trackingProgress } = trackingSlice.actions;

export default trackingSlice.reducer;
